import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Save, RefreshCw, User, Briefcase, MapPin,
  Shield, CheckCircle2, AlertCircle, Plus, X, PoundSterling,
  FileText, Calendar, LogOut, Home
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = getApiBaseUrl();

const AVAILABLE_TRADES = [
  'plumbing', 'electrical', 'gas', 'heating', 'carpentry',
  'painting', 'roofing', 'locksmith', 'pest_control', 'cleaning', 'general'
];

const ContractorAccount = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [isNewProfile, setIsNewProfile] = useState(false);
  
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  
  // Form state
  const [formData, setFormData] = useState({
    business_name: '',
    bio: '',
    trades: [],
    service_postcodes: [],
    hourly_rate: '',
    call_out_fee: '',
    gas_safe_number: '',
    electrical_cert_number: '',
    insurance_expiry: '',
    emergency_available: false,
  });
  
  const [newPostcode, setNewPostcode] = useState('');

  const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/contractors/profile`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setFormData({
          business_name: data.business_name || '',
          bio: data.bio || '',
          trades: data.trades || [],
          service_postcodes: data.service_postcodes || [],
          hourly_rate: data.hourly_rate?.toString() || '',
          call_out_fee: data.call_out_fee?.toString() || '',
          gas_safe_number: data.gas_safe_number || '',
          electrical_cert_number: data.electrical_cert_number || '',
          insurance_expiry: data.insurance_expiry || '',
          emergency_available: data.emergency_available || false,
        });
        setIsNewProfile(false);
      } else if (response.status === 404) {
        setIsNewProfile(true);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleSave = async () => {
    if (!formData.trades.length) {
      toast.error('Please select at least one trade');
      return;
    }
    if (!formData.service_postcodes.length) {
      toast.error('Please add at least one service area postcode');
      return;
    }

    setSaving(true);
    try {
      const endpoint = isNewProfile 
        ? `${API_URL}/api/contractors/profile`
        : `${API_URL}/api/contractors/profile`;
      
      const method = isNewProfile ? 'POST' : 'PATCH';
      
      const response = await fetch(endpoint, {
        method,
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
          call_out_fee: formData.call_out_fee ? parseFloat(formData.call_out_fee) : null,
        })
      });
      
      if (response.ok) {
        toast.success(isNewProfile ? 'Profile created!' : 'Profile saved!');
        fetchProfile();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save profile');
      }
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForVerification = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contractors/profile/submit-for-verification`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        toast.success('Submitted for verification!');
        fetchProfile();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit');
      }
    } catch (error) {
      toast.error('Failed to submit');
    }
  };

  const toggleTrade = (trade) => {
    setFormData(prev => ({
      ...prev,
      trades: prev.trades.includes(trade)
        ? prev.trades.filter(t => t !== trade)
        : [...prev.trades, trade]
    }));
  };

  const addPostcode = () => {
    if (newPostcode && !formData.service_postcodes.includes(newPostcode.toUpperCase())) {
      setFormData(prev => ({
        ...prev,
        service_postcodes: [...prev.service_postcodes, newPostcode.toUpperCase().slice(0, 4)]
      }));
      setNewPostcode('');
    }
  };

  const removePostcode = (postcode) => {
    setFormData(prev => ({
      ...prev,
      service_postcodes: prev.service_postcodes.filter(p => p !== postcode)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8" data-testid="contractor-account">
      {/* Top Navigation Bar */}
      <div className="bg-white border-b border-slate-200 fixed top-0 left-0 right-0 z-50 px-4 md:px-8 py-3">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <Link to="/" className="flex items-center gap-2">
            <div className="p-1.5 bg-blue-600 rounded-lg">
              <Home className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-bold text-blue-600">LetWise</h1>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/contractor/dashboard">
              <Button variant="ghost" size="sm">
                <User className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleLogout}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid="contractor-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Header - with top margin to account for fixed navbar */}
      <div className="mb-6 mt-16">
        <Link to="/contractor/dashboard" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Dashboard
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {isNewProfile ? 'Complete Your Profile' : 'My Account'}
            </h1>
            <p className="text-slate-600">
              {isNewProfile 
                ? 'Set up your contractor profile to start receiving job offers'
                : 'Manage your profile and settings'
              }
            </p>
          </div>
          {profile && (
            <div className="flex items-center gap-2">
              {profile.verified ? (
                <Badge className="bg-green-100 text-green-700 border-green-200">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Verified
                </Badge>
              ) : (
                <Badge variant="outline" className="border-amber-300 text-amber-700">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Pending Verification
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Business Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Business Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Business Name</label>
                <Input
                  placeholder="Your business name"
                  value={formData.business_name}
                  onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Bio / Description</label>
                <Textarea
                  placeholder="Tell landlords about your experience and services..."
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          {/* Trades */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Trades & Services *
              </CardTitle>
              <CardDescription>Select all trades you can provide</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {AVAILABLE_TRADES.map((trade) => (
                  <Button
                    key={trade}
                    variant={formData.trades.includes(trade) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleTrade(trade)}
                  >
                    {formData.trades.includes(trade) && <CheckCircle2 className="h-3 w-3 mr-1" />}
                    {trade.replace('_', ' ')}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Service Areas */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Service Areas *
              </CardTitle>
              <CardDescription>Add postcode prefixes you cover (e.g., EH, G, AB)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Input
                  placeholder="e.g., EH"
                  value={newPostcode}
                  onChange={(e) => setNewPostcode(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && addPostcode()}
                  className="w-32"
                  maxLength={4}
                />
                <Button onClick={addPostcode} variant="outline">
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.service_postcodes.map((postcode) => (
                  <Badge 
                    key={postcode} 
                    variant="secondary"
                    className="px-3 py-1 flex items-center gap-1"
                  >
                    {postcode}
                    <button onClick={() => removePostcode(postcode)}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                {formData.service_postcodes.length === 0 && (
                  <p className="text-sm text-slate-500">No service areas added yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Rates */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PoundSterling className="h-5 w-5" />
                Rates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Hourly Rate (£)</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Call-out Fee (£)</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.call_out_fee}
                    onChange={(e) => setFormData({ ...formData, call_out_fee: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Certifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Certifications & Insurance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Gas Safe Number</label>
                  <Input
                    placeholder="123456"
                    value={formData.gas_safe_number}
                    onChange={(e) => setFormData({ ...formData, gas_safe_number: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Electrical Cert Number</label>
                  <Input
                    placeholder="NICEIC/NAPIT number"
                    value={formData.electrical_cert_number}
                    onChange={(e) => setFormData({ ...formData, electrical_cert_number: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Insurance Expiry Date</label>
                <Input
                  type="date"
                  value={formData.insurance_expiry}
                  onChange={(e) => setFormData({ ...formData, insurance_expiry: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex gap-3">
            <Button onClick={handleSave} disabled={saving} className="flex-1">
              {saving ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {isNewProfile ? 'Create Profile' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Profile Status */}
          <Card>
            <CardHeader>
              <CardTitle>Profile Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Profile Created</span>
                  {profile ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-amber-500" />
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Trades Selected</span>
                  {formData.trades.length > 0 ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-amber-500" />
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Service Areas</span>
                  {formData.service_postcodes.length > 0 ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-amber-500" />
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Verified</span>
                  {profile?.verified ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-amber-500" />
                  )}
                </div>
              </div>

              {profile && !profile.verified && (
                <Button 
                  onClick={handleSubmitForVerification}
                  className="w-full mt-4"
                  variant="outline"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Submit for Verification
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Help */}
          <Card>
            <CardHeader>
              <CardTitle>Need Help?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600">
                Complete your profile to start receiving job offers. Jobs will be matched based on your trades and service areas.
              </p>
              <ul className="mt-3 text-sm text-slate-600 space-y-1">
                <li>• Add all trades you can work on</li>
                <li>• Add postcode prefixes you cover</li>
                <li>• Include certifications for regulated work</li>
                <li>• Keep insurance details up to date</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContractorAccount;
